import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tab,
  Tabs,
  Alert,
  AlertTitle,
  CircularProgress,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  DirectionsBoat as BoatIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
  Today as TodayIcon,
  ConfirmationNumber as TicketIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  EventAvailable as EventIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getAllReservations, updateReservation } from '../../firebase/services/reservationService';
import { getActiveVessels } from '../../firebase/services/vesselService';
import { Reservation, Vessel } from '../../models/types';
import { format, isPast, isToday, addDays, parseISO, startOfWeek, endOfWeek, isWithinInterval } from 'date-fns';
import { SelectChangeEvent } from '@mui/material/Select';
import { Link } from 'react-router-dom';
import { formatTime12Hour } from '../../utils/formatters';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// Helper function to chunk an array into rows of a given size
function chunk<T>(array: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const StaffDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Load reservations and vessels
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all reservations and vessels
        const [fetchedReservations, fetchedVessels] = await Promise.all([
          getAllReservations(),
          getActiveVessels()
        ]);
        
        console.log('🔍 Staff Dashboard - Fetched reservations:', fetchedReservations.length);
        console.log('🔍 Reservation details:', fetchedReservations.map(res => ({
          id: res.id,
          date: res.date,
          customerName: `${res.customerDetails?.firstName} ${res.customerDetails?.lastName}`,
          status: res.status,
          vesselType: res.vesselType
        })));
        
        setReservations(fetchedReservations);
        setVessels(fetchedVessels);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Date filter handler
  const handleDateChange = (newDate: unknown) => {
    setSelectedDate(newDate as Date | null);
  };

  // Status filter handler
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  // Search handler
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Update reservation status
  const handleUpdateStatus = async (reservationId: string, newStatus: 'confirmed' | 'cancelled') => {
    try {
      await updateReservation(reservationId, { status: newStatus });
      
      // Update the local state to reflect the change
      setReservations(prevReservations => 
        prevReservations.map(res => 
          res.id === reservationId ? { ...res, status: newStatus } : res
        )
      );
      
      setSuccessMessage(`Reservation ${reservationId.substring(0, 8)} has been ${newStatus}.`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      console.error(`Error updating reservation status:`, err);
      setError(`Failed to update reservation status. Please try again.`);
    }
  };

  // Filter reservations based on active filters
  const filteredReservations = reservations.filter(reservation => {
    let includeReservation = true;
    
    // Date filter
    if (selectedDate) {
      // Parse reservation date as local date to avoid timezone issues
      const [year, month, day] = reservation.date.split('-').map(Number);
      const reservationDate = new Date(year, month - 1, day);
      const reservationDateStr = format(reservationDate, 'yyyy-MM-dd');
      const filterDate = format(selectedDate, 'yyyy-MM-dd');
      includeReservation = includeReservation && reservationDateStr === filterDate;
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      includeReservation = includeReservation && reservation.status === statusFilter;
    }
    
    // Search term filter
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      const matchesCustomerName = 
        `${reservation.customerDetails.firstName} ${reservation.customerDetails.lastName}`
          .toLowerCase()
          .includes(searchTermLower);
      const matchesVesselName = reservation.vesselName.toLowerCase().includes(searchTermLower);
      const matchesId = reservation.id.toLowerCase().includes(searchTermLower);
      const matchesEmail = reservation.customerDetails.email.toLowerCase().includes(searchTermLower);
      
      includeReservation = includeReservation && (
        matchesCustomerName || matchesVesselName || matchesId || matchesEmail
      );
    }
    
    return includeReservation;
  });

  // Helper function to parse reservation date as local date
  const parseReservationDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  // Helper function to parse 12-hour time format (e.g., "10:00 AM") to 24-hour format
  const parseTime12Hour = (timeStr: string): number => {
    if (!timeStr) return 0;
    
    const [time, period] = timeStr.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    let hour24 = hours;
    if (period === 'PM' && hours !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hours === 12) {
      hour24 = 0;
    }
    
    return hour24 * 60 + (minutes || 0); // Convert to minutes since midnight
  };

  // Get today's reservations for the first tab
  const todayReservations = reservations.filter(reservation => {
    // Parse date as local date to avoid timezone issues
    const [year, month, day] = reservation.date.split('-').map(Number);
    const reservationDate = new Date(year, month - 1, day);
    const today = new Date();
    
    const reservationDateStr = format(reservationDate, 'yyyy-MM-dd');
    const todayStr = format(today, 'yyyy-MM-dd');
    
    // Only show today's reservations that are not finalized (checked-out or completed)
    const isToday = reservationDateStr === todayStr;
    const isFinalized = reservation.checkInStatus === 'checked-out' || reservation.status === 'completed';
    
    return isToday && !isFinalized;
  }).sort((a, b) => {
    // Sort by start time in ascending order using proper time comparison
    if (a.startTime && b.startTime) {
      const aTimeMinutes = parseTime12Hour(a.startTime);
      const bTimeMinutes = parseTime12Hour(b.startTime);
      return aTimeMinutes - bTimeMinutes;
    } else if (a.startTime) {
      return -1; // a has start time, b doesn't, so a comes first
    } else if (b.startTime) {
      return 1; // b has start time, a doesn't, so b comes first
    }
    return 0; // neither has start time, maintain original order
  });

  // Get upcoming reservations for the second tab
  const upcomingReservations = reservations.filter(reservation => {
    // Parse date as local date instead of UTC to avoid timezone issues
    const [year, month, day] = reservation.date.split('-').map(Number);
    const reservationDate = new Date(year, month - 1, day); // month is 0-indexed
    
    const isPastRes = isPast(reservationDate);
    const isTodayRes = isToday(reservationDate);
    const shouldInclude = !isPastRes && !isTodayRes;
    
    console.log('📅 Upcoming Filter Debug:', {
      reservationId: reservation.id,
      date: reservation.date,
      reservationDate: reservationDate,
      currentDate: new Date(),
      isPast: isPastRes,
      isToday: isTodayRes,
      shouldInclude: shouldInclude,
      customerName: `${reservation.customerDetails?.firstName} ${reservation.customerDetails?.lastName}`
    });
    
    return shouldInclude;
  }).sort((a, b) => {
    // Sort by date in ascending order
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  // Calculate statistics
  const today = new Date();
  const weekStart = startOfWeek(today);
  const weekEnd = endOfWeek(today);

  const weekReservations = reservations.filter(res => {
    const resDate = parseISO(res.date);
    return isWithinInterval(resDate, { start: weekStart, end: weekEnd });
  });

  const completedReservations = reservations.filter(res => 
    res.checkInStatus === 'checked-out' || res.status === 'completed'
  );

  const pendingReservations = reservations.filter(res => 
    res.status === 'pending'
  );

  // Get all today's reservations for stats (including finalized ones)
  const allTodayReservations = reservations.filter(reservation => {
    const [year, month, day] = reservation.date.split('-').map(Number);
    const reservationDate = new Date(year, month - 1, day);
    const today = new Date();
    
    const reservationDateStr = format(reservationDate, 'yyyy-MM-dd');
    const todayStr = format(today, 'yyyy-MM-dd');
    return reservationDateStr === todayStr;
  });

  const totalRevenue = reservations.reduce((sum, res) => 
    sum + (res.payment?.amount || 0), 0
  );

  const todayRevenue = allTodayReservations.reduce((sum, res) => 
    sum + (res.payment?.amount || 0), 0
  );

  const weekRevenue = weekReservations.reduce((sum, res) => 
    sum + (res.payment?.amount || 0), 0
  );

  const checkedInToday = allTodayReservations.filter(res => 
    res.checkInStatus === 'checked-in'
  );

  const upcomingToday = allTodayReservations.filter(res => 
    res.checkInStatus !== 'checked-in' && res.checkInStatus !== 'checked-out'
  );

  // Helper to capitalize each word
  const toTitleCase = (str: string) => str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

  // Compute future reservations (date/time after now), sorted ascending
  const now = new Date();
  const futureReservations = reservations
    .filter(res => {
      const resDate = parseISO(res.date);
      // Combine date and startTime for accurate comparison
      let start = resDate;
      if (res.startTime) {
        const [h, m] = res.startTime.split(":").map(Number);
        start = new Date(resDate);
        start.setHours(h, m || 0, 0, 0);
      }
      return start > now;
    })
    .sort((a, b) => {
      const aDate = parseISO(a.date);
      const bDate = parseISO(b.date);
      let aStart = aDate;
      let bStart = bDate;
      if (a.startTime) {
        const [h, m] = a.startTime.split(":").map(Number);
        aStart = new Date(aDate);
        aStart.setHours(h, m || 0, 0, 0);
      }
      if (b.startTime) {
        const [h, m] = b.startTime.split(":").map(Number);
        bStart = new Date(bDate);
        bStart.setHours(h, m || 0, 0, 0);
      }
      return aStart.getTime() - bStart.getTime();
    });

  // Compute reservations for tomorrow and the day after tomorrow (exclude today)
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dayAfterTomorrow = new Date(tomorrow);
  dayAfterTomorrow.setDate(tomorrow.getDate() + 2);

  const next2DaysReservations = reservations
    .filter(res => {
      const resDate = parseISO(res.date);
      return resDate >= tomorrow && resDate < dayAfterTomorrow;
    })
    .sort((a, b) => {
      const aDate = parseISO(a.date);
      const bDate = parseISO(b.date);
      
      // First compare by date
      if (aDate.getTime() !== bDate.getTime()) {
        return aDate.getTime() - bDate.getTime();
      }
      
      // If dates are equal, compare by start time
      const aTimeMinutes = parseTime12Hour(a.startTime || '');
      const bTimeMinutes = parseTime12Hour(b.startTime || '');
      
      return aTimeMinutes - bTimeMinutes;
    });

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {format(currentTime, 'EEEE')}         {format(currentTime, 'MMMM d, yyyy')}         {format(currentTime, 'h:mm a')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      </Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Quick Stats Cards */}
        <Grid item xs={12} sm={6} md={4}>
          <Button
            component={Link}
            to="/staff/reservations"
            sx={{ 
              width: '100%',
              textAlign: 'left',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <Card sx={{ 
              width: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EventIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Today's Schedule</Typography>
                </Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {allTodayReservations.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Reservations
                </Typography>
              </CardContent>
            </Card>
          </Button>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Button
            component={Link}
            to="/staff/checkin?tab=1"
            sx={{ 
              width: '100%',
              textAlign: 'left',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <Card sx={{ 
              width: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">Checked In</Typography>
                </Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {checkedInToday.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Launches
                </Typography>
              </CardContent>
            </Card>
          </Button>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Button
            component={Link}
            to="/staff/checkin?tab=0"
            sx={{ 
              width: '100%',
              textAlign: 'left',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <Card sx={{ 
              width: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ArrowForwardIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Upcoming</Typography>
                </Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {upcomingToday.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Check-Ins
                </Typography>
              </CardContent>
            </Card>
          </Button>
        </Grid>
      </Grid>

      {/* Today's Schedule */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Today's Schedule
        </Typography>
        {todayReservations.length === 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography color="text.secondary">No reservations scheduled for today</Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {todayReservations.map((reservation) => {
              const isCheckedIn = reservation.checkInStatus === 'checked-in';
              
              return (
                <Grid item xs={12} sm={6} key={reservation.id}>
                  <Paper 
                    sx={{ 
                      p: 2,
                      backgroundColor: isCheckedIn ? 'grey.100' : 'background.paper',
                      opacity: isCheckedIn ? 0.7 : 1,
                      transition: 'all 0.2s ease'
                    }} 
                    variant="outlined"
                  >
                  <Typography variant="subtitle1">
                    {reservation.startTime && reservation.endTime && (
                      <>{formatTime12Hour(reservation.startTime)} - {formatTime12Hour(reservation.endTime)}</>
                    )}
                    {reservation.startTime && !reservation.endTime && (
                      <>{formatTime12Hour(reservation.startTime)}</>
                    )}
                    {!reservation.startTime && !reservation.endTime && (
                      <>All Day</>
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    {reservation.customerDetails.firstName} {reservation.customerDetails.lastName}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {reservation.customerDetails.phone}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {toTitleCase(reservation.vesselType || '')} &bull; Party Size: {reservation.partySize}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Chip
                      size="small"
                      label={reservation.status}
                      color={
                        reservation.status === 'confirmed' ? 'success' :
                        reservation.status === 'pending' ? 'warning' :
                        'error'
                      }
                    />
                    {reservation.checkInStatus && (
                      <Chip
                        size="small"
                        label={reservation.checkInStatus}
                        color={reservation.checkInStatus === 'checked-in' ? 'success' : 'default'}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
            })}
          </Grid>
        )}
      </Paper>

      {/* Overview */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Next 2 Days Reservations
        </Typography>
        {next2DaysReservations.length === 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography>No reservations for the next 2 days</Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {next2DaysReservations.map((reservation) => (
              <Grid item xs={12} sm={6} key={reservation.id}>
                <Paper sx={{ p: 2 }} variant="outlined">
                  <Typography variant="subtitle1">
                    {format(parseISO(reservation.date), 'EEEE, MMMM d')}
                    {reservation.startTime && reservation.endTime && (
                      <> &bull; {formatTime12Hour(reservation.startTime)} - {formatTime12Hour(reservation.endTime)}</>
                    )}
                    {reservation.startTime && !reservation.endTime && (
                      <> &bull; {formatTime12Hour(reservation.startTime)}</>
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    {reservation.customerDetails.firstName} {reservation.customerDetails.lastName}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {reservation.customerDetails.phone}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {toTitleCase(reservation.vesselType || '')} &bull; Party Size: {reservation.partySize}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default StaffDashboard; 